<template>
  <!-- 新版本优化样式 -->
  <div v-if="newVersion" class="steps-container-v2" :class="{ 'steps-preview-mode': clickablePreview }">
    <div class="steps-wrapper-v2" :class="wrapperClass" :style="wrapperStyle" ref="stepsWrapper">
      <Steps :active="value" :direction="direction" :class="['custom-steps', { 'has-description': hasDescription }]">
        <template v-if="direction == 'horizontal'">
          <Step
            v-for="(item, index) in options"
            :key="item.title"
            :class="['custom-step', getStepClickableClass(index), getStepPositionClass(index)]"
            :style="getHorizontalStepStyle(item, index)"
            @click.native="handleStepClick(index)"
          >
            <div :class="['step-title',{'step-titleNoHasDescription':!item.description}]" :title="item.title" :style="titleStyle">{{ item.title }}</div>
            <div
              class="step-horizontalDescription"
              :title="item.description"
            >
              {{ item.description || '' }}
            </div>
          </Step>
        </template>
        <template v-if="direction == 'vertical'">
          <Step
            v-for="(item, index) in options"
            :key="item.title"
            :class="['custom-step', getStepClickableClass(index)]"
            @click.native="handleStepClick(index)"
          >
            <div class="step-content">
              <div class="step-title" :title="item.title" :style="titleStyle">{{ item.title }}</div>
              <div class="step-description" :title="item.description" v-if="item.description">
                {{ item.description }}
              </div>
            </div>
          </Step>
        </template>
      </Steps>
    </div>

    <!-- 悬浮左箭头 -->
    <div
      v-if="showArrows && direction === 'horizontal' && canScrollLeft"
      class="scroll-arrow left-arrow"
      @click="scrollLeft"
    >
      <svg
        viewBox="0 0 24 24"
        width="16"
        height="16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M15 18L9 12L15 6"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </div>

    <!-- 悬浮右箭头 -->
    <div
      v-if="showArrows && direction === 'horizontal' && canScrollRight"
      class="scroll-arrow right-arrow"
      @click="scrollRight"
    >
      <svg
        viewBox="0 0 24 24"
        width="16"
        height="16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M9 18L15 12L9 6"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </div>
  </div>

  <!-- 原版本样式 -->
  <div v-else class="steps-container-v1" :class="{ 'steps-preview-mode': clickablePreview }">
    <Steps :active="value" :direction="direction">
      <template v-if="direction == 'horizontal'">
        <Step
          v-for="(item, index) in options"
          :key="item.title"
          :class="getStepClickableClass(index)"
          @click.native="handleStepClick(index)"
        >{{ item.title }}</Step>
      </template>
      <template v-if="direction == 'vertical'">
        <Step
          v-for="(item, index) in options"
          :key="item.title"
          :class="getStepClickableClass(index)"
          @click.native="handleStepClick(index)"
        >
          <div>{{ item.title }}</div>
          <div>{{ item.description }}</div>
        </Step>
      </template>
    </Steps>
  </div>
</template>

<script>
import { Steps, Step } from 'vant';
export default {
  components: { Steps, Step },
  props: {
    value: {
      type: Number,
    },
    direction: {
      type: String,
      default: 'horizontal',
    },
    options: {
      type: Array,
      default: () => [],
    },
    newVersion: {
      type: Boolean,
      default: false,
    },
    newVersionMarginBottom: {
      type: String,
      default: '10',
    },
    newVersionDescriptionFontSize: {
      type: String,
      default: '12',  
    },
    // newVersionDescriptionFontColor: {
    //   type: String,
    //   default: 'abc987',
    // },
    // 新版本专用props
    allowMultiline: {
      type: Boolean,
      default: false,
    },
    maxHeight: {
      type: String,
      default: '400px',
    },
    maxTitleWidth: {
      type: String,
      default: '180px',
    },
    enableScroll: {
      type: Boolean,
      default: false, // true: 同时开启滚动条和箭头, false: 同时关闭
    },
    // 是否允许点击步骤跳转
    clickable: {
      type: Boolean,
      default: false,
    },
    // 是否禁用组件（禁用后不可点击）
    disabled: {
      type: Boolean,
      default: false,
    },
    // 是否启用预览点击模式
    // 启用后：点击步骤只会触发 preview-change 事件，不会改变实际进度（value）
    // 再次点击已选中的步骤会取消选中
    // 在 Formily 中使用时，预览索引会自动写入到 {fieldName}_preview 字段中
    clickablePreview: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      canScrollLeft: false,
      canScrollRight: false,
      // 预览点击选中的步骤索引，null 表示未选中任何步骤
      previewIndex: null,
    };
  },
  computed: {
    // 计算是否显示滚动条
    showScrollbar() {
      return this.enableScroll;
    },
    // 计算是否显示箭头
    showArrows() {
      return this.enableScroll;
    },
    // 计算是否有任何步骤包含描述
    hasDescription() {
      return this.options.some(item => item.description);
    },
    wrapperClass() {
      if (!this.newVersion) return {};
      return {
        'steps-horizontal': this.direction === 'horizontal',
        'steps-vertical': this.direction === 'vertical',
        'allow-multiline': this.allowMultiline,
        'hide-scrollbar': !this.showScrollbar,
      };
    },
    wrapperStyle() {
      if (!this.newVersion) return {};
      const style = {
        '--margin-bottom': `${this.newVersionMarginBottom}px`,
        '--description-font-size': `${this.newVersionDescriptionFontSize}px`,
        '--step-title-max-width': this.normalizedMaxTitleWidth,
        '--steps-horizontal-min-width': `${this.horizontalStepsMinWidth}px`,
        // '--description-font-color': `#${this.newVersionDescriptionFontColor}`,
      };
      if (this.direction === 'vertical') {
        style.maxHeight = this.maxHeight;
      }
      return style;
    },
    normalizedMaxTitleWidth() {
      const width = String(this.maxTitleWidth || '').trim();
      if (!width || width === 'auto') {
        return '180px';
      }
      if (/^\d+(\.\d+)?$/.test(width)) {
        return `${width}px`;
      }
      return width;
    },
    horizontalStepsMinWidth() {
      if (!this.newVersion || this.direction !== 'horizontal') {
        return 0;
      }
      if (this.options.length === 1) {
        return this.getStepDisplayWidth(this.options[0]);
      }
      const totalWidth = this.options.reduce((total, item, index) => {
        if (index === this.options.length - 1) {
          return total;
        }
        return total + this.getStepItemWidth(item, index);
      }, 0);
      return Math.max(totalWidth, 72);
    },
    titleStyle() {
      if (!this.newVersion) return {};
      return {
        maxWidth: this.normalizedMaxTitleWidth,
      };
    },
  },
  mounted() {
    if (this.newVersion && this.showArrows && this.direction === 'horizontal') {
      this.$nextTick(() => {
        this.checkScrollability();
        this.scrollToActiveStep();
        if (this.$refs.stepsWrapper) {
          this.$refs.stepsWrapper.addEventListener('scroll', this.checkScrollability);
        }
        window.addEventListener('resize', this.checkScrollability);
      });
    } else if (this.newVersion) {
      this.$nextTick(() => {
        this.scrollToActiveStep();
      });
    }
  },
  beforeDestroy() {
    if (this.$refs.stepsWrapper) {
      this.$refs.stepsWrapper.removeEventListener('scroll', this.checkScrollability);
    }
    window.removeEventListener('resize', this.checkScrollability);
  },
  watch: {
    options: {
      handler() {
        if (this.newVersion) {
          this.$nextTick(() => {
            this.checkScrollability();
            this.scrollToActiveStep();
          });
        }
      },
      deep: true,
    },
    value() {
      if (this.newVersion) {
        this.$nextTick(() => {
          this.checkScrollability();
          this.scrollToActiveStep();
        });
      }
    },
  },
  methods: {
    getTextDisplayWidth(text, fontSize = 14) {
      const content = String(text || '');
      if (!content) {
        return 0;
      }
      return Array.from(content).reduce((total, char) => {
        if (/[^\x00-\xff]/.test(char)) {
          return total + fontSize;
        }
        if (/\s/.test(char)) {
          return total + Math.ceil(fontSize * 0.35);
        }
        return total + Math.ceil(fontSize * 0.65);
      }, 0);
    },
    getStepDisplayWidth(item) {
      const descriptionFontSize = Number(this.newVersionDescriptionFontSize) || 12;
      const titleWidth = this.getTextDisplayWidth(item?.title, 14) + 16;
      const descriptionWidth = this.getTextDisplayWidth(item?.description, descriptionFontSize) + 12;
      return Math.max(72, titleWidth, Math.min(descriptionWidth, 112));
    },
    getStepItemWidth(item, index) {
      const currentWidth = this.getStepDisplayWidth(item);
      const isFirstStep = index === 0;
      const isLastStep = index === this.options.length - 1;
      if (isLastStep) {
        return currentWidth;
      }
      const nextItem = this.options[index + 1];
      const nextWidth = this.getStepDisplayWidth(nextItem);
      const nextIsLastStep = index + 1 === this.options.length - 1;
      const currentRightWidth = isFirstStep ? currentWidth : currentWidth / 2;
      const nextLeftWidth = nextIsLastStep ? nextWidth : nextWidth / 2;
      return Math.max(72, Math.ceil(currentRightWidth + nextLeftWidth + 16));
    },
    getStepPositionClass(index) {
      if (index === 0) {
        return 'step-first';
      }
      if (index === this.options.length - 1) {
        return 'step-last';
      }
      return 'step-middle';
    },
    getHorizontalStepStyle(item, index) {
      if (!this.newVersion || this.direction !== 'horizontal') {
        return {};
      }
      return {
        '--step-item-width': `${this.getStepItemWidth(item, index)}px`,
        '--step-title-width': `${this.getStepDisplayWidth(item)}px`,
      };
    },
    scrollToActiveStep() {
      if (!this.newVersion || !this.$refs.stepsWrapper || this.value === undefined) {
        return;
      }

      this.$nextTick(() => {
        const wrapper = this.$refs.stepsWrapper;
        const stepsElement = wrapper.querySelector('.van-steps');

        if (!stepsElement) return;

        const stepElements = stepsElement.querySelectorAll('.van-step');
        const activeStep = stepElements[this.value];

        if (!activeStep) return;

        if (this.direction === 'horizontal') {
          const stepLeft = activeStep.offsetLeft;
          const firstStepWidth = stepElements[0]?.offsetWidth || 0;

          const scrollPosition = stepLeft - firstStepWidth;

          wrapper.scrollTo({
            left: scrollPosition,
            behavior: 'smooth',
          });
        } else {
          const stepTop = activeStep.offsetTop;
          const firstStepHeight = stepElements[0]?.offsetHeight || 0;

          const scrollPosition = stepTop - firstStepHeight;

          wrapper.scrollTo({
            top: scrollPosition,
            behavior: 'smooth',
          });
        }
      });
    },
    checkScrollability() {
      if (!this.newVersion || !this.$refs.stepsWrapper) return;

      const wrapper = this.$refs.stepsWrapper;
      const scrollLeft = wrapper.scrollLeft;
      const scrollWidth = wrapper.scrollWidth;
      const clientWidth = wrapper.clientWidth;

      this.canScrollLeft = scrollLeft > 0;
      this.canScrollRight = scrollLeft < scrollWidth - clientWidth - 1;
    },
    scrollLeft() {
      if (!this.newVersion || !this.$refs.stepsWrapper) return;
      const wrapper = this.$refs.stepsWrapper;
      wrapper.scrollBy({ left: -200, behavior: 'smooth' });
    },
    scrollRight() {
      if (!this.newVersion || !this.$refs.stepsWrapper) return;
      const wrapper = this.$refs.stepsWrapper;
      wrapper.scrollBy({ left: 200, behavior: 'smooth' });
    },
    // 判断某个step是否允许被点击（普通点击模式）
    isStepClickable(index) {
      if (!this.clickable || this.disabled) return false;
      const current = Number(this.value) || 0;
      // 只允许点击当前步骤的上一步、下一步，或当前步骤之前（已完成）的步骤
      return index <= current + 1 && index !== current;
    },
    // 判断某个step是否允许被预览点击
    isStepPreviewClickable() {
      if (!this.clickablePreview || this.disabled) return false;
      // 预览模式下所有步骤都可以点击
      return true;
    },
    // 为step添加可点击/禁用的样式类
    getStepClickableClass(index) {
      // 预览点击模式
      if (this.clickablePreview) {
        if (this.disabled) return 'step-disabled';
        const classes = ['step-preview-clickable'];
        // 添加预览选中状态（使用 step-preview-selected 与全局 CSS 配合）
        if (this.previewIndex === index) {
          classes.push('step-preview-selected');
        }
        return classes.join(' ');
      }
      // 普通点击模式
      if (!this.clickable) return '';
      if (this.disabled) return 'step-disabled';
      return this.isStepClickable(index) ? 'step-clickable' : 'step-not-clickable';
    },
    // 处理step点击事件
    handleStepClick(index) {
      // 预览点击模式
      if (this.clickablePreview && !this.disabled) {
        this.handlePreviewClick(index);
        return;
      }
      // 普通点击模式
      if (!this.isStepClickable(index)) return;
      this.$emit('input', index);
      this.$emit('change', index);
      this.$emit('step-click', index);
    },
    // 处理预览点击
    handlePreviewClick(index) {
      // 再次点击已选中的步骤会取消选中
      const newPreviewIndex = this.previewIndex === index ? null : index;
      this.previewIndex = newPreviewIndex;
      
      // 触发 preview-change 事件，参数为目标步骤索引（取消时为 null）
      // 在 Formily 包装组件中会自动将此值写入到 {fieldName}_preview 字段
      this.$emit('preview-change', newPreviewIndex);
    },
    // 获取预览索引（供外部读取）
    getPreviewIndex() {
      return this.previewIndex;
    },
    // 重置预览索引
    resetPreviewIndex() {
      this.previewIndex = null;
      this.$emit('preview-change', null);
    },
  },
};
</script>
<style lang='less' scoped>
// 新版本优化样式
.steps-container-v2 {
  position: relative;
  width: 100%;
}

// 悬浮滚动箭头样式
.scroll-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  background: rgba(255, 255, 255, 0.95);
  border: 2px solid #d9d9d9;
  border-radius: 50%;
  display: flex !important;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1000; // 提高层级确保悬浮
  color: #666;
  // box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
  // backdrop-filter: blur(4px); // 添加背景模糊效果

  &:hover {
    background: rgba(255, 255, 255, 1);
    color: #1890ff;
    border-color: #1890ff;
    // box-shadow: 0 6px 20px rgba(24, 144, 255, 0.3);
    transform: translateY(-50%) scale(1.05);
  }

  &:active {
    transform: translateY(-50%) scale(0.95);
  }

  &.left-arrow {
    left: 16px; // 悬浮时可以更靠近边缘

    // // 添加渐变遮罩效果
    // &::before {
    //   content: '';
    //   position: absolute;
    //   top: 50%;
    //   left: 100%;
    //   transform: translateY(-50%);
    //   width: 30px;
    //   height: 60px;
    //   background: linear-gradient(to right, rgba(255, 255, 255, 0.8), transparent);
    //   pointer-events: none;
    //   z-index: -1;
    // }

    @media (max-width: 768px) {
      left: 12px;
    }

    @media (max-width: 480px) {
      left: 8px;
    }
  }

  &.right-arrow {
    right: 16px; // 悬浮时可以更靠近边缘

    // // 添加渐变遮罩效果
    // &::before {
    //   content: '';
    //   position: absolute;
    //   top: 50%;
    //   right: 100%;
    //   transform: translateY(-50%);
    //   width: 30px;
    //   height: 60px;
    //   background: linear-gradient(to left, rgba(255, 255, 255, 0.8), transparent);
    //   pointer-events: none;
    //   z-index: -1;
    // }

    @media (max-width: 768px) {
      right: 12px;
    }

    @media (max-width: 480px) {
      right: 8px;
    }
  }

  svg {
    pointer-events: none;
    display: block;
    flex-shrink: 0;
  }

  @media (max-width: 768px) {
    width: 36px;
    height: 36px;

    svg {
      width: 14px;
      height: 14px;
    }
  }

  @media (max-width: 480px) {
    width: 32px;
    height: 32px;

    svg {
      width: 12px;
      height: 12px;
    }
  }
}

.steps-wrapper-v2 {
  width: 100%;
  overflow: hidden;

  &.steps-horizontal {
    overflow-x: auto;
    overflow-y: hidden;

    // 水平滚动条样式优化
    &::-webkit-scrollbar {
      height: 4px;
    }

    &::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 2px;
    }

    &::-webkit-scrollbar-thumb {
      background: #c1c1c1;
      border-radius: 2px;

      &:hover {
        background: #a8a8a8;
      }
    }

    .custom-steps {
      min-width: max-content;
      padding-bottom: 8px;
      // padding-right: 120px; // 进一步增加右边距，确保最后一个节点的连接线完整显示

      @media (max-width: 768px) {
        // padding-right: 100px;
      }

      @media (max-width: 480px) {
        // padding-right: 80px;
      }
    }
    /deep/ .custom-steps .van-steps__items {
      margin-bottom: var(--margin-bottom);
      padding: 0;
    }
  }

  &.steps-vertical {
    max-height: 400px;
    overflow-y: auto;
    overflow-x: hidden;

    // 垂直滚动条样式优化
    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 2px;
    }

    &::-webkit-scrollbar-thumb {
      background: #c1c1c1;
      border-radius: 2px;

      &:hover {
        background: #a8a8a8;
      }
    }
  }

  &.hide-scrollbar {
    &::-webkit-scrollbar {
      display: none;
    }

    -ms-overflow-style: none;
    scrollbar-width: none;
  }
}

// 自定义步骤样式
.custom-step {
  position: relative;
  box-sizing: border-box;
  .step-titleNoHasDescription{
    margin-bottom: 32px !important;
  }
  .step-title {
    max-width: 180px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 14px;
    line-height: 1.4;
    color: #333;
    word-break: break-word;
    overflow-wrap: break-word;
    box-sizing: border-box;
    display: flex;
    align-items: center; // 垂直居中对齐
    min-height: 20px; // 确保有足够高度
    padding: 0; // 移除内边距避免错位
    margin: 0; // 移除外边距避免错位
    text-align:center;
    // 响应式字体大小
    @media (max-width: 768px) {
      max-width: 140px;
      font-size: 12px;
    }

    @media (max-width: 480px) {
      max-width: 100px;
      font-size: 11px;
    }
  }

  .step-content {
    max-width: 220px;
    box-sizing: border-box;
    padding-right: 12px;

    @media (max-width: 768px) {
      max-width: 180px;
      padding-right: 10px;
    }

    @media (max-width: 480px) {
      max-width: 140px;
      padding-right: 8px;
    }
  }

  .step-horizontalDescription {
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: var(--description-font-size);
    line-height: 1.3;
    // color: var(--description-font-color);
        // color: #666;
    margin-top: 22px;
    word-break: break-word;
    overflow-wrap: break-word;
    box-sizing: border-box;
    text-align:center;

    // 响应式字体大小
    @media (max-width: 768px) {
      max-width: 160px;
      font-size: 11px;
    }

    @media (max-width: 480px) {
      max-width: 120px;
      font-size: 10px;
    }
  }
  &:last-child {
    min-width: auto !important;
    position: absolute;
  }
  .step-description {
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 12px;
    line-height: 1.3;
    color: #666;
    margin-top: 4px;
    word-break: break-word;
    overflow-wrap: break-word;
    box-sizing: border-box;

    // 响应式字体大小
    @media (max-width: 768px) {
      max-width: 160px;
      font-size: 11px;
    }

    @media (max-width: 480px) {
      max-width: 120px;
      font-size: 10px;
    }
  }
  &:last-child {
    min-width: auto !important;
    position: absolute;
  }
}

// Vant Steps 组件样式覆盖（仅新版本）
.steps-container-v2 {
  :deep(.van-steps) {
    &.van-steps--horizontal {
      .van-step {
        flex-shrink: 0;
        min-width: 140px; // 增加最小宽度

        @media (max-width: 768px) {
          min-width: 120px;
        }

        @media (max-width: 480px) {
          min-width: 100px;
        }

        // 确保步骤内容不重叠
        .van-step__content {
          display: flex;
          align-items: center;
          box-sizing: border-box;
          width: 100%;
          padding: 0; // 移除默认内边距
        }

        // 步骤标题容器
        .van-step__title {
          word-break: break-word;
          overflow-wrap: break-word;
          hyphens: auto;
          display: flex;
          align-items: center;
          min-height: 20px;
          margin: 0;
          padding: 0;
        }

        // 步骤连接线的间距调整
        .van-step__line {
          margin-left: 8px; // 减少左边距
          margin-right: 8px; // 减少右边距
          min-width: 120px !important; // 强制设置连接线最小宽度
          width: auto !important; // 自适应宽度
          flex: 1 !important; // 让连接线自适应宽度
          display: block !important; // 确保显示

          @media (max-width: 768px) {
            margin-left: 6px;
            margin-right: 6px;
            min-width: 100px !important;
          }

          @media (max-width: 480px) {
            margin-left: 4px;
            margin-right: 4px;
            min-width: 80px !important;
          }
        }

        // 步骤圆圈和文字的对齐
        .van-step__circle {
          margin-right: 8px; // 圆圈与文字的间距
        }

        // 步骤标题的对齐
        .van-step__title {
          display: flex;
          align-items: center;
          min-height: 20px; // 确保文字垂直居中
        }

        // 特别处理最后一个步骤
        &:last-child {
          // 确保最后一个步骤有足够的空间
          min-width: 200px !important;
          padding-right: 80px !important;

          .van-step__line {
            min-width: 150px !important; // 强制设置更长的连接线
            width: 150px !important; // 固定宽度
            display: block !important; // 强制显示连接线
            visibility: visible !important;
            opacity: 1 !important;

            @media (max-width: 768px) {
              min-width: 120px !important;
              width: 120px !important;
            }

            @media (max-width: 480px) {
              min-width: 100px !important;
              width: 100px !important;
            }
          }

          // 修复文字对齐
          .van-step__content {
            display: flex !important;
            align-items: center !important;
            width: auto !important;
          }

          .van-step__title {
            margin: 0 !important;
            padding: 0 !important;
            display: flex !important;
            align-items: center !important;
          }

          @media (max-width: 768px) {
            min-width: 180px !important;
            // padding-right: 60px !important;
          }

          @media (max-width: 480px) {
            min-width: 160px !important;
            // padding-right: 50px !important;
          }
        }

        // 不是最后一个步骤时增加右边距
        &:not(:last-child) {
          margin-right: 24px; // 增加步骤间距

          @media (max-width: 768px) {
            margin-right: 20px;
          }

          @media (max-width: 480px) {
            margin-right: 16px;
          }
        }
      }
    }

    &.van-steps--vertical {
      .van-step {
        margin-bottom: 20px; // 增加垂直间距

        &:last-child {
          margin-bottom: 0;
        }

        // 垂直方向内容间距
        .van-step__content {
          padding-bottom: 16px;
          padding-right: 12px;
        }
      }
    }
  }
}

// 水平方向特殊优化
.steps-horizontal {
  .custom-steps {
    display: flex;
    align-items: center; // 改为居中对齐
    min-width: max-content;
    // padding-right: 100px; // 确保最后一个步骤有足够空间
  }

  .custom-step {
    flex-shrink: 0;
    min-width: 120px; // 适中的最小宽度
    max-width: 200px; // 适中的最大宽度
    display: flex;
    align-items: center; // 垂直居中

    @media (max-width: 768px) {
      min-width: 100px;
      max-width: 160px;
    }

    @media (max-width: 480px) {
      min-width: 80px;
      max-width: 120px;
    }

    // 步骤标题样式重置
    .step-title {
      padding: 0;
      margin: 0;
      flex: 1;
    }
  }
}

// 垂直方向特殊优化
.steps-vertical {
  .custom-step {
    margin-bottom: 16px;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

// 多行显示模式
.steps-wrapper-v2.allow-multiline {
  .custom-step {
    min-height: 60px;

    @media (max-width: 768px) {
      min-height: 50px;
    }

    @media (max-width: 480px) {
      min-height: 40px;
    }
  }

  .step-title,
  .step-description {
    white-space: normal;
    word-break: break-word;
    overflow-wrap: break-word;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    hyphens: auto;
    line-height: 1.3;
  }

  .step-title {
    -webkit-line-clamp: 2;
    line-clamp: 2;
    max-height: 2.6em;
    margin-bottom: 4px;
  }

  .step-description {
    -webkit-line-clamp: 2;
    line-clamp: 2;
    max-height: 2.6em;
  }

  // 水平方向多行显示时的特殊处理
  &.steps-horizontal {
    .custom-step {
      min-width: 160px;
      max-width: 240px;

      @media (max-width: 768px) {
        min-width: 140px;
        max-width: 200px;
      }

      @media (max-width: 480px) {
        min-width: 120px;
        max-width: 180px;
      }
    }
  }
}

// 强制单行显示模式
.steps-wrapper-v2:not(.allow-multiline) {
  .step-title {
    display: block;
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
  }

  .step-description {
    display: block;
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
  }
}

// 强制修复最后一个步骤的连接线问题
.steps-container-v2 {
  :deep(.van-steps--horizontal) {
    // 倒数第二个步骤的连接线宽度调整为200%
    .van-step:nth-last-child(2) {
      .van-step__line {
        width: 200% !important;
      }
    }

    .van-step:last-child {
      // 最后一个步骤的最小宽度调整为auto
      min-width: auto !important;
      .van-step__line {
        min-width: 150px !important;
        width: 150px !important;
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
        background-color: #ebedf0 !important; // 确保连接线可见
        height: 1px !important;

        &::after {
          content: '' !important;
          display: block !important;
          width: 100% !important;
          height: 1px !important;
          background-color: #ebedf0 !important;
        }

        @media (max-width: 768px) {
          min-width: 120px !important;
          width: 120px !important;
        }

        @media (max-width: 480px) {
          min-width: 100px !important;
          width: 100px !important;
        }
      }

      // 修复最后一个步骤的文字对齐
      .van-step__content {
        display: flex !important;
        align-items: center !important;
        justify-content: flex-start !important;
      }

      .van-step__title {
        margin: 0 !important;
        padding: 0 !important;
        line-height: 20px !important;
        display: flex !important;
        align-items: center !important;
      }
    }
  }
}

// 水平单行模式：按文本长度自适应宽度；首节点左对齐，末节点右对齐，中间节点居中
.steps-wrapper-v2.steps-horizontal:not(.allow-multiline) {
  .custom-steps {
    min-width: var(--steps-horizontal-min-width, max-content) !important;
  }

  .custom-step {
    flex: 0 0 var(--step-item-width, 72px) !important;
    min-width: var(--step-item-width, 72px) !important;
    max-width: none !important;
    margin-right: 0 !important;
    padding-right: 0 !important;
    position: relative;
  }

  .custom-step.step-last {
    right: 1px !important;
    width: var(--step-title-width, 72px) !important;
    min-width: var(--step-title-width, 72px) !important;
    max-width: none !important;
    position: absolute !important;
  }

  .step-title,
  .step-horizontalDescription {
    width: var(--step-title-width, auto);
    max-width: none !important;
    min-width: 0;
    overflow: visible !important;
    display: block;
    text-overflow: clip !important;
    white-space: nowrap !important;
  }

  .custom-step.step-first {
    .step-title,
    .step-horizontalDescription {
      text-align: left;
    }
  }

  .custom-step.step-middle {
    .step-title,
    .step-horizontalDescription {
      text-align: center;
    }
  }

  .custom-step.step-last {
    .step-title,
    .step-horizontalDescription {
      text-align: right;
    }
  }

  /deep/ .custom-step .van-step__content,
  /deep/ .custom-step .van-step__title {
    width: var(--step-title-width, auto) !important;
    max-width: none !important;
    min-width: 0;
    overflow: visible !important;
    display: block !important;
    margin-left: 0 !important;
    padding: 0 !important;
  }

  /deep/ .custom-step.step-first .van-step__title,
  /deep/ .custom-step.step-last .van-step__title {
    transform: none !important;
  }

  /deep/ .custom-step.step-middle .van-step__title {
    transform: translateX(-50%) !important;
  }

  /deep/ .custom-step .van-step__line {
    min-width: 0 !important;
    width: 100% !important;
    margin-left: 8px !important;
    margin-right: 8px !important;
    flex: none !important;
    display: block !important;
  }

  /deep/ .custom-step.step-last .van-step__line {
    min-width: 0 !important;
    width: 0 !important;
    display: none !important;
  }
}

// 可点击状态相关样式
.custom-step {
  &.step-clickable {
    cursor: pointer;

    &:hover {
      .step-title {
        color: #1677ff;
      }
    }
  }

  &.step-not-clickable {
    cursor: not-allowed;
  }

  &.step-disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  // 预览点击模式样式
  // 注意：预览模式下只有圆圈图标高亮，标题和描述不变色
  &.step-preview-clickable {
    cursor: pointer;
  }
}

// 预览选中状态样式 - 直接在 scoped 中通过 :deep() 穿透
// 只在开启 clickablePreview 模式时生效
.steps-container-v2.steps-preview-mode {
  :deep(.van-step.step-preview-selected) {
    // 圆圈图标高亮效果
    .van-step__circle-container {
      .van-step__circle {
        border-color: #1677ff !important;
        color: #1677ff !important;
        transform: scale(1.15);
        transition: all 200ms cubic-bezier(0.4, 0, 0.2, 1);
        box-shadow: 0 0 0 2px #1677ff;
        background-color: transparent !important;
      }
      .van-step__icon {
        color: #1677ff !important;
        transform: scale(1.15);
        transition: all 200ms cubic-bezier(0.4, 0, 0.2, 1);
      }
    }

    // 已完成步骤被预览选中时
    &.van-step--finish .van-step__circle-container .van-step__icon {
      color: #1677ff !important;
      transform: scale(1.15);
    }

    // 当前步骤被预览选中时
    &.van-step--process .van-step__circle-container .van-step__circle {
      border-color: #1677ff !important;
      background-color: #1677ff !important;
      transform: scale(1.15);
    }
  }
}
</style>

<!-- 非 scoped 样式，用于预览选中效果（确保能穿透到 Vant 组件内部） -->
<style lang="less">
/* 预览选中状态样式 - 只在开启 clickablePreview 模式时生效 */
.steps-preview-mode {
  .van-step.step-preview-selected {
    /* 圆圈图标高亮效果 */
    .van-step__circle-container {
      .van-step__circle {
        border-color: #1677ff !important;
        color: #1677ff !important;
        transform: scale(1.15);
        transition: all 200ms cubic-bezier(0.4, 0, 0.2, 1);
        box-shadow: 0 0 0 2px #1677ff;
        background-color: transparent !important;
      }
      .van-step__icon {
        color: #1677ff !important;
        transform: scale(1.15);
        transition: all 200ms cubic-bezier(0.4, 0, 0.2, 1);
      }
    }

    /* 已完成步骤被预览选中时 */
    &.van-step--finish .van-step__circle-container .van-step__icon {
      color: #1677ff !important;
      transform: scale(1.15);
    }

    /* 当前步骤被预览选中时 */
    &.van-step--process .van-step__circle-container .van-step__circle {
      border-color: #1677ff !important;
      background-color: #1677ff !important;
      transform: scale(1.15);
    }
  }

  /* 预览点击模式下的鼠标指针样式 */
  .van-step.step-preview-clickable {
    cursor: pointer;
    
    &:hover .van-step__circle-container {
      .van-step__circle,
      .van-step__icon {
        transform: scale(1.1);
        transition: transform 150ms cubic-bezier(0.4, 0, 0.2, 1);
      }
    }
  }
}
</style>